import React from 'react';
import { useTranslation } from 'react-i18next';
import { IoTrophyOutline } from 'react-icons/io5';
import type { WorkoutAnnualRecord } from '../services/api';
import { workoutDisplayDigits as displayDigits, formatWorkoutNumber } from '../utils/workoutNumberFormat';
import './workout-annual-records.css';

function peakParts(type: string) {
  const match = /^(.*?)(\d+)(sec|min|hour)$/.exec(type);
  return { metric: match?.[1] ?? type, amount: Number(match?.[2] ?? 0), unit: match?.[3] ?? '',
    seconds: Number(match?.[2] ?? 0) * (match?.[3] === 'hour' ? 3600 : match?.[3] === 'min' ? 60 : 1) };
}

export function WorkoutAnnualRecords({ records }: { records?: WorkoutAnnualRecord[] }) {
  const { t, i18n } = useTranslation();
  if (!records?.length) return null;
  const locale = i18n?.resolvedLanguage ?? i18n?.language ?? 'en';
  const number = (value: number, digits = 1) => formatWorkoutNumber(value, locale, { maximumFractionDigits: digits, useGrouping: false });
  const groups = new Map<string, WorkoutAnnualRecord[]>();
  for (const record of records) {
    const metric = peakParts(record.peak_type).metric;
    groups.set(metric, [...(groups.get(metric) ?? []), record]);
  }
  const years = [...new Set(records.map(record => record.year))].sort().map(year => number(year, 0)).join('، ');
  return <section className="workout-annual-records" tabIndex={-1} aria-label={t('annualRecords.title')}>
    <header className="workout-annual-records__header">
      <span className="workout-annual-records__trophy"><IoTrophyOutline aria-hidden="true" /></span>
      <div><h3>{t('annualRecords.achievement', { count: records.length, countLabel: number(records.length, 0) })}</h3>
        <p>{t('annualRecords.context', { year: years })}</p></div>
    </header>
    {[...groups].map(([metric, group]) => <div className="workout-annual-records__group" key={metric}>
      <table>
        <caption>{t(`annualRecords.${metric}`, { defaultValue: metric })}</caption>
        <thead><tr><th scope="col">{t('annualRecords.duration')}</th><th scope="col">{t('annualRecords.newRecord')}</th><th scope="col">{t('annualRecords.improvement')}</th></tr></thead>
        <tbody>{[...group].sort((a, b) => peakParts(a.peak_type).seconds - peakParts(b.peak_type).seconds).map(record => {
          const peak = peakParts(record.peak_type);
          const unit = t(`annualRecords.unit_${metric}`, { defaultValue: '' });
          const format = (value: number) => metric === 'pace'
            ? displayDigits(`${Math.floor(Math.round(value) / 60)}:${String(Math.round(value) % 60).padStart(2, '0')}`, locale)
            : number(value);
          const percent = formatWorkoutNumber(record.improvement_percentage / 100, locale, { style: 'percent', maximumFractionDigits: 1 });
          return <tr key={record.peak_type}>
            <th scope="row">{peak.unit ? `${number(peak.amount, 0)} ${t(`annualRecords.${peak.unit}`)}` : record.peak_type}</th>
            <td><strong><bdi>{format(record.value)}</bdi> <span>{unit}</span></strong>
              <small>{t('annualRecords.previousShort')}: <bdi>{format(record.previous_value)}</bdi> {unit}</small></td>
            <td className="workout-annual-records__improvement"><span aria-hidden="true">↑ </span>{t('annualRecords.better', { percent })}</td>
          </tr>;
        })}</tbody>
      </table>
    </div>)}
  </section>;
}
